import { useState, useRef, useEffect } from "react";
import {
  Modal,
  Card,
  CardTitle,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  Col,
} from "reactstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { CardBody, CardImg } from "reactstrap/lib";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "react-query";
import { EditProgramLicense } from "../../actions/superUserMutations";
import { fetchCoursesForAdmin } from "../../actions/coursesActions";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

const ModifyProgramLicenseModal = ({
  isOpen,
  toggle,
  intl,
  licenseId,
  licenseName,
  licenseImage,
}) => {
  const inputRef = useRef(null);
  const [newLicenseName, setNewLicenseName] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const dispatch = useDispatch();
  const editProgramLicense = useMutation(EditProgramLicense);

  const validateImageFile = (file) => {
    const allowedTypes = [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/svg+xml",
    ];

    if (!allowedTypes.includes(file.type)) return "FileType";

    return null;
  };

  const handleRemoveImageFile = () => {
    setNewFile(null);
    setFileError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleImageFile = (selectedFile) => {
    if (!selectedFile) return;
    setNewFile(selectedFile);

    const error = validateImageFile(selectedFile);
    setFileError(error);
  };

  const handleSaveNewNameOrImage = () => {
    if (fileError) return;

    if (newLicenseName.trim() === licenseName && !newFile) return;

    const formData = new FormData();

    formData.append("Id", licenseId);
    formData.append("LicenseName", newLicenseName.trim() || licenseName);
    if (newFile) {
      formData.append("ImageFile", newFile);
    }

    editProgramLicense.mutate(formData, {
      onSuccess: (res) => {
        if (res && res.status > 199 && res.status < 300) {
          dispatch(fetchCoursesForAdmin());
          toast.success("Licens uppdaterat");
          clearData();
        } else {
          toast.warn(res.response.data);
        }
      },
      onError: (data) => {
        toast.warn(data.response.data);
      },
    });
    toggle();
  };

  const clearData = () => {
    setFileError("");
    setNewFile(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    setNewLicenseName(licenseName);
  }, [licenseName]);

  return (
    <Modal isOpen={isOpen} toggle={() => toggle()}>
      <ModalBody>
        <CardTitle>
          <FormattedMessage id="Edit" />
        </CardTitle>
        <Card>
          <CardBody>
            <Label for="LicenseName">
              <FormattedMessage id="LicenseName" />:
            </Label>
            <Input
              name="LicensName"
              type="text"
              onChange={(e) => setNewLicenseName(e.target.value)}
              value={newLicenseName}
            ></Input>
            <Label for="LicenseImage">
              <FormattedMessage id="LicenseImage" />:
            </Label>
            <CardImg
              src={
                newFile ? URL.createObjectURL(newFile) : "img/" + licenseImage
              }
              alt="Card image cap"
              width="100%"
            />
            <div className="d-flex align-items-center">
              <Input
                name="Upload Image"
                type="file"
                onChange={(e) => handleImageFile(e.target.files[0])}
                className="form-control mt-2 mb-2"
                innerRef={inputRef}
              ></Input>
              {newFile && (
                <FontAwesomeIcon
                  icon="trash"
                  className="ms-2 mb-2"
                  style={{
                    cursor: "pointer",
                    transition: "color 0.2s",
                    color: "black",
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.color = "red")}
                  onMouseOut={(e) => (e.currentTarget.style.color = "black")}
                  title={intl.formatMessage({ id: "RemoveImage" })}
                  onClick={handleRemoveImageFile}
                />
              )}
            </div>
            {fileError === "FileType" && (
              <Col className="text-danger">
                <FormattedMessage id="FileType" />
              </Col>
            )}
          </CardBody>
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          outline
          onClick={() => {
            clearData();
            toggle();
          }}
        >
          <FormattedMessage id="Cancel" />
        </Button>
        <Button
          color="primary"
          outline
          disabled={fileError}
          onClick={handleSaveNewNameOrImage}
        >
          <FormattedMessage id="Save" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(ModifyProgramLicenseModal);
